import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMissatgeError } from '@/api/errors';
import {
  useCalendaris,
  useNoms,
  useResolSollicitud,
  useSollicituds,
  useTorn,
  useTorns,
  type EstatSollicitud,
  type SollicitudCanviDto,
} from '@/api/planificacio';
import { Icona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Avatar, Camp } from '@/pages/operacio/comu';
import { Permis } from '@/permisos/Permis';
import { dataCurta, solapats } from './calendari';
import './planificacio.css';

const ESTATS: (EstatSollicitud | '')[] = ['PENDENT', 'APROVADA', 'REBUTJADA', ''];
const BADGE: Record<EstatSollicitud, string> = {
  PENDENT: 'incident',
  APROVADA: 'ok',
  REBUTJADA: 'critic',
  CANCELLADA: 'gray',
};

function BadgeEstatSollicitud({ estat }: { estat: EstatSollicitud }) {
  const { t } = useTranslation('planificacio');
  return <span className={`badge badge--${BADGE[estat]}`}>{t(`estatSollicitud.${estat}`)}</span>;
}

/** "Sol·licitant → Proposat" (o només el sol·licitant si és un canvi d'horari). */
function useTitolSollicitud() {
  const noms = useNoms();
  return (s: SollicitudCanviDto) =>
    s.proposatId != null
      ? `${noms(s.sollicitantId)} → ${noms(s.proposatId)}`
      : noms(s.sollicitantId);
}

function ItemSollicitud({
  s,
  actiu,
  onTria,
}: {
  s: SollicitudCanviDto;
  actiu: boolean;
  onTria: () => void;
}) {
  const f = useFormat();
  const noms = useNoms();
  const titol = useTitolSollicitud();
  const torn = useTorn(s.tornId);
  const calendaris = useCalendaris();
  const calendari = calendaris.data?.find((c) => c.id === torn.data?.calendariId);
  const inici = s.nouInici ?? torn.data?.inici;
  const fi = s.nouFi ?? torn.data?.fi;
  return (
    <button
      type="button"
      className="list-row safata__item"
      aria-pressed={actiu}
      data-sollicitud={s.id}
      onClick={onTria}
    >
      <Avatar nom={noms(s.sollicitantId)} />
      <span className="list-row__text">
        <span className="list-row__label" style={{ display: 'block' }}>
          {titol(s)}
        </span>
        <span className="list-row__sub" style={{ display: 'block' }}>
          {inici && fi
            ? `${dataCurta(inici, f.idioma)} — ${dataCurta(fi, f.idioma)}`
            : f.data(s.createdAt)}
          {calendari ? ` · ${calendari.nom}` : ''}
        </span>
      </span>
      <BadgeEstatSollicitud estat={s.estat} />
    </button>
  );
}

function Detall({ s }: { s: SollicitudCanviDto }) {
  const { t } = useTranslation('planificacio');
  const f = useFormat();
  const noms = useNoms();
  const missatgeError = useMissatgeError();
  const torn = useTorn(s.tornId);
  const calendaris = useCalendaris();
  const resol = useResolSollicitud();
  const [comentari, setComentari] = useState('');
  const calendari = calendaris.data?.find((c) => c.id === torn.data?.calendariId);
  const inici = s.nouInici ?? torn.data?.inici ?? '';
  const fi = s.nouFi ?? torn.data?.fi ?? '';
  const veins = useTorns(torn.data?.calendariId ?? NaN, inici, fi);
  const resultant = torn.data
    ? {
        ...torn.data,
        inici: inici || torn.data.inici,
        fi: fi || torn.data.fi,
        titularId:
          s.tipus === 'SUBSTITUCIO' || s.tipus === 'INTERCANVI'
            ? (s.proposatId ?? torn.data.titularId)
            : torn.data.titularId,
      }
    : null;
  const conflictes = resultant ? solapats([resultant], veins.data ?? []) : 0;
  const pendent = s.estat === 'PENDENT';

  return (
    <div className="panel" data-sollicitud-detall={s.id}>
      <div className="panel__head">
        <Avatar nom={noms(s.sollicitantId)} mida="m" />
        <div style={{ minWidth: 0 }}>
          <h3>{t(`sollicituds.titolTipus.${s.tipus}`, { nom: noms(s.sollicitantId) })}</h3>
          <div className="t-caption-2 dim">
            {[calendari?.nom, t('sollicituds.sollicitatEl', { data: f.data(s.createdAt) })]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
        <div className="spacer" />
        <BadgeEstatSollicitud estat={s.estat} />
      </div>
      <div className="panel__body">
        <div className="grid grid-2" style={{ gap: 12 }}>
          <div className="camp">
            <span className="camp__label">{t('sollicituds.tornAfectat')}</span>
            <span className="t-subhead">
              {torn.data
                ? `${f.dataHora(torn.data.inici)} — ${f.dataHora(torn.data.fi)}`
                : `#${s.tornId}`}
            </span>
          </div>
          <div className="camp">
            <span className="camp__label">{t('sollicituds.motiu')}</span>
            <span className="t-subhead">{s.motiu || '—'}</span>
          </div>
          {s.nouInici || s.nouFi ? (
            <div className="camp">
              <span className="camp__label">{t('sollicituds.novaFranja')}</span>
              <span className="t-subhead">
                {s.nouInici ? f.dataHora(s.nouInici) : '—'} — {s.nouFi ? f.dataHora(s.nouFi) : '—'}
              </span>
            </div>
          ) : null}
          <div className="camp">
            <span className="camp__label">{t('sollicituds.tipus')}</span>
            <span className="t-subhead">{t(`tipusSollicitud.${s.tipus}`)}</span>
          </div>
        </div>
        <hr className="hr" />
        <div className="grid grid-2" style={{ gap: 12 }}>
          <div>
            <div className="mini-titol">{t('sollicituds.sollicitant')}</div>
            <div className="hstack gap-8">
              <Avatar nom={noms(s.sollicitantId)} />
              <div className="list-row__text">
                <div className="list-row__label">{noms(s.sollicitantId)}</div>
                <div className="list-row__sub">
                  {torn.data?.titularId === s.sollicitantId
                    ? t('sollicituds.titularActual')
                    : t('sollicituds.substitutActual')}
                </div>
              </div>
            </div>
          </div>
          {s.proposatId != null ? (
            <div>
              <div className="mini-titol">{t('sollicituds.proposat')}</div>
              <div className="hstack gap-8">
                <Avatar nom={noms(s.proposatId)} />
                <div className="list-row__text">
                  <div className="list-row__label">{noms(s.proposatId)}</div>
                  <div className="list-row__sub">{t(`tipusSollicitud.${s.tipus}`)}</div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
        {s.comentariResolucio ? (
          <>
            <hr className="hr" />
            <div className="camp">
              <span className="camp__label">{t('sollicituds.comentari')}</span>
              <span className="t-subhead">{s.comentariResolucio}</span>
            </div>
          </>
        ) : null}
        {pendent && resultant ? (
          <>
            <hr className="hr" />
            <div className="mini-titol">{t('sollicituds.coberturaResultant')}</div>
            <div className="hstack gap-8" data-conflictes={conflictes}>
              <Icona
                nom={conflictes ? 'exclamationmark-triangle' : 'checkmark-circle'}
                className={`icon-18 ${conflictes ? 'c-orange' : 'c-green'}`}
              />
              <span className="t-footnote">
                {conflictes
                  ? t('sollicituds.ambConflictes', { count: conflictes })
                  : t('sollicituds.senseConflictes', { nom: noms(resultant.titularId) })}
              </span>
            </div>
          </>
        ) : null}
        {pendent ? (
          <Permis recurs="cai.sollicituds" tipus="MODIFICAR">
            <div className="mt" />
            <Camp etiqueta={t('sollicituds.comentariOpcional')}>
              <textarea
                className="input"
                rows={2}
                placeholder={t('sollicituds.comentariPlaceholder')}
                value={comentari}
                onChange={(e) => setComentari(e.target.value)}
              />
            </Camp>
            <div className="hstack gap-8 mt">
              <button
                type="button"
                className="btn btn--s"
                disabled={resol.isPending}
                onClick={() => resol.mutate({ id: s.id, accio: 'rebutjar', comentari })}
              >
                <Icona nom="xmark-circle" />
                {t('sollicituds.denega')}
              </button>
              <button
                type="button"
                className="btn btn--success btn--s"
                disabled={resol.isPending}
                onClick={() => resol.mutate({ id: s.id, accio: 'aprovar', comentari })}
              >
                <Icona nom="checkmark-circle" />
                {t('sollicituds.aprova')}
              </button>
            </div>
          </Permis>
        ) : null}
        {resol.isError ? <p className="error-text">{missatgeError(resol.error)}</p> : null}
      </div>
    </div>
  );
}

export function W14Sollicituds() {
  const { t } = useTranslation('planificacio');
  const noms = useNoms();
  const [estat, setEstat] = useState<EstatSollicitud | ''>('PENDENT');
  const [text, setText] = useState('');
  const [seleccio, setSeleccio] = useState<number | null>(null);
  const sollicituds = useSollicituds(estat);
  const totesPendents = useSollicituds('PENDENT');
  const llista = (sollicituds.data ?? []).filter(
    (s) =>
      !text.trim() ||
      `${noms(s.sollicitantId)} ${s.proposatId != null ? noms(s.proposatId) : ''} ${s.motiu ?? ''}`
        .toLowerCase()
        .includes(text.trim().toLowerCase()),
  );
  const actual = llista.find((s) => s.id === seleccio) ?? llista[0];
  const pendents = (totesPendents.data ?? []).length;

  return (
    <section className="pantalla" data-screen="W-14">
      <Toolbar
        titol={t('titols.canvisTorn')}
        badges={
          <span className="badge badge--incident">
            {t('sollicituds.pendents', { count: pendents })}
          </span>
        }
      >
        <label className="search pla-cerca">
          <Icona nom="magnifier" />
          <input
            type="search"
            placeholder={t('sollicituds.cerca')}
            aria-label={t('sollicituds.cerca')}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
      </Toolbar>
      <div className="content safata">
        <div className="panel">
          <div className="panel__head" style={{ padding: '10px 12px' }}>
            <div className="segmented" style={{ width: '100%' }}>
              {ESTATS.map((e) => (
                <button
                  key={e || 'TOTES'}
                  type="button"
                  style={{ flex: 1 }}
                  aria-pressed={estat === e}
                  onClick={() => setEstat(e)}
                >
                  {e ? t(`estatSollicitud.${e}`) : t('sollicituds.totes')}
                </button>
              ))}
            </div>
          </div>
          {sollicituds.isError ? (
            <ErrorCarrega onReintenta={() => void sollicituds.refetch()} />
          ) : llista.length === 0 ? (
            <div className="empty">
              <Icona nom="tray" />
              {sollicituds.isLoading ? t('comu.carregant') : t('sollicituds.buit')}
            </div>
          ) : (
            <div>
              {llista.map((s) => (
                <ItemSollicitud
                  key={s.id}
                  s={s}
                  actiu={actual?.id === s.id}
                  onTria={() => setSeleccio(s.id)}
                />
              ))}
            </div>
          )}
        </div>
        {actual ? (
          <Detall key={actual.id} s={actual} />
        ) : (
          <div className="panel empty">
            <Icona nom="arrow-swap" />
            {t('sollicituds.tria')}
          </div>
        )}
      </div>
    </section>
  );
}
